/** @jsxImportSource @emotion/react */
'use client'

import Typography from '@mui/material/Typography'
import type React from 'react'
import { FlexCol, FlexRow } from '../layout'
import { styles } from './styles'

export type StatCardTone = 'light' | 'dark' | 'mint'

export interface StatCardShellProps {
	/** Small uppercase label shown next to the accent dot. */
	kicker: string
	/** Surface treatment. Defaults to `light`. */
	tone?: StatCardTone
	/** Optional element rendered on the right of the header row (e.g. a badge). */
	badge?: React.ReactNode
	/** Optional footer detail row, divided by a hairline. */
	footer?: React.ReactNode
	/** Focal content — the large value and its supporting copy. */
	children: React.ReactNode
}

/**
 * Shared shell that fixes the hierarchy for every stat card so they sit
 * uniformly as carousel slides:
 *
 *   kicker (dot + label)  ·  optional badge
 *   ─────────────────────────────────────
 *   focal value + supporting copy  (vertically centred)
 *   ─────────────────────────────────────
 *   optional footer detail
 */
export default function StatCardShell({
	kicker,
	tone = 'light',
	badge,
	footer,
	children,
}: StatCardShellProps) {
	return (
		<article css={[styles.card, styles.tone[tone]]}>
			<FlexCol gap="lg" css={styles.fill}>
				<FlexRow justify="between" css={styles.headerRow}>
					<FlexRow gap="sm" css={styles.headerRow}>
						<Typography
							component="span"
							css={[styles.kicker, styles.kickerTone[tone]]}
						>
							{kicker}
						</Typography>
					</FlexRow>
					{badge}
				</FlexRow>

				<div css={styles.body}>{children}</div>

				{footer && (
					<div css={[styles.footer]}>{footer}</div>
				)}
			</FlexCol>
		</article>
	)
}
